import * as THREE from 'three';

import { DigitalTwinOverlay, type OverlayTerrainContext } from './digital-twin-overlay.js';
import { FrameWorkQueue } from './frame-work-queue.js';
import { PreparationWorkerClient } from './preparation-worker-client.js';
import { QuantizedMeshLoader } from './quantized-mesh-loader.js';
import { SWISS_PROJECTION } from './swiss-projection.js';
import { TerrainDraper } from './terrain-drape.js';
import { TerrainLodPlanner } from './terrain-lod-planner.js';
import {
  VectorTileFeatureProvider,
  type VectorTileRequester,
} from './vector-tile-feature-provider.js';
import { VectorTileOverlay } from './vector-tile-overlay.js';
import { WmtsTextureProvider, type WmtsTextureResult } from './wmts-texture-provider.js';
import { environment } from '../../../environments/environment.js';
import type { PointCloudViewer } from '../../services/pointcloud-runtime.js';
import { LruCache } from '../../shared/lru-cache.js';
import { DigitalTwinFeature } from '../../types/digital-twin.js';
import type { GeoBounds, QueuedTile, TerrainTileMesh } from '../../types/terrain-types.js';
import type { VectorTileLayerConfig } from '../../types/vector-tile-layer.js';

interface VectorTileLayerEntry {
  uri: string;
  maxZoom: number;
  requestTile: VectorTileRequester;
  overlay: VectorTileOverlay;
  provider: VectorTileFeatureProvider;
  enabled: boolean;
}

export { WMTS_TEXTURE_CACHE_ENTRIES } from './wmts-texture-provider.js';
export const FAILED_TILE_CACHE_ENTRIES = 1000;

const MIN_ZOOM = 5;
const MAX_ZOOM = 17;

/** Camera must move at least this far before the tile set is recomputed. */
const CAMERA_MOVE_EPSILON_DEG = 0.0005;
const CAMERA_MOVE_EPSILON_M = 20;

/**
 * Manages quantized mesh terrain tiles from the Swiss terrain service.
 *
 * Owns the tile lifecycle — scheduling, loading, transformation and eviction —
 * and delegates the surrounding concerns to focused collaborators:
 * {@link TerrainLodPlanner} decides which tiles should be resident,
 * {@link WmtsTextureProvider} supplies their imagery, and
 * {@link DigitalTwinOverlay} renders feature geometry draped on the surface.
 */
export class QuantizedMeshTerrainManager {
  viewer: PointCloudViewer;
  enabled = false;
  loading = false;

  readonly baseUrl: string;
  readonly apiVersion: string;

  readonly loader = new QuantizedMeshLoader();
  readonly textures: WmtsTextureProvider;
  readonly lod: TerrainLodPlanner;
  readonly overlay: DigitalTwinOverlay;

  // Internal vector tile layers draped on the terrain, keyed by layer id.
  private readonly vectorTileLayers = new Map<string, VectorTileLayerEntry>();

  readonly terrainGroup: THREE.Group;

  activeTiles = new Map<string, TerrainTileMesh>();
  visibleTiles = new Set<string>();
  loadingTiles = new Map<string, AbortController>();
  failedTiles = new LruCache<string, number>(FAILED_TILE_CACHE_ENTRIES);

  // Monotonic generation token. Incremented whenever the manager is reset
  // (dispose / layer switch / terrain reload) so in-flight async work can bail
  // before mutating a stale world.
  private generation = 0;

  // Re-entrancy guard for the async update pass.
  private updateInFlight = false;

  maxActiveTiles = 300;
  maxConcurrentLoads = 6;
  private loadQueue: QueuedTile[] = [];
  private activeLoadCount = 0;

  private lastCameraLon: number | null = null;
  private lastCameraLat: number | null = null;
  private lastCameraHeight: number | null = null;

  /** Retry failed tiles after this delay. */
  private readonly failedTileRetryDelay = 5000;

  // How long a tile that left the visible set is kept around before it is
  // removed. Keeps a short overlap so newly requested tiles can load without
  // leaving holes, but prevents stale coarse tiles from blurring the view.
  private readonly staleTileGrace = 2000;

  lv95Origin: THREE.Vector3 | null = null;
  metadata: Record<string, unknown> | null = null;
  private lastUpdate: number | undefined;
  private totalVertices = 0;

  private readonly draper = new TerrainDraper(() => this.requireOrigin());
  private readonly preparation = new PreparationWorkerClient();
  private readonly workQueue = new FrameWorkQueue();
  private readonly vectorRequests = new Map<string, AbortController>();
  private readonly textureReloads = new Map<TerrainTileMesh, AbortController>();

  constructor(viewer: PointCloudViewer) {
    this.viewer = viewer;

    this.baseUrl = environment.terrain.quantizedMeshBaseUrl;
    this.apiVersion = environment.terrain.quantizedMeshApiVersion;

    this.lod = new TerrainLodPlanner(MIN_ZOOM, MAX_ZOOM);
    this.textures = new WmtsTextureProvider(
      () => this.viewer.renderer?.capabilities?.getMaxAnisotropy() || 4,
    );

    this.terrainGroup = new THREE.Group();
    this.terrainGroup.name = 'QuantizedMeshTerrain';

    this.overlay = new DigitalTwinOverlay(this.buildOverlayContext());

    if (this.viewer.scene?.scene) {
      this.viewer.scene.scene.add(this.terrainGroup);
      this.viewer.scene.scene.add(this.overlay.group);
    }
  }

  /**
   * Terrain state shared with every overlay draped on the surface (digital
   * twin features, internal vector tile layers, ...).
   */
  private buildOverlayContext(): OverlayTerrainContext {
    return {
      requireOrigin: () => this.requireOrigin(),
      hasOrigin: () => this.lv95Origin !== null,
      draper: this.draper,
      preparation: this.preparation,
      workQueue: this.workQueue,
      rendererCanvas: () => this.viewer.renderer?.domElement,
    };
  }

  /**
   * Active WMTS layer id. Kept for callers that configure the layer before the
   * first load, when there are no tiles to reload yet.
   */
  get wmtsLayer(): string {
    return this.textures.layer;
  }

  set wmtsLayer(layer: string) {
    this.textures.layer = layer;
  }

  get wmtsLayerMap(): string {
    return this.textures.layerMap;
  }

  get wmtsLayerAerial(): string {
    return this.textures.layerAerial;
  }

  // ─── Origin ────────────────────────────────────────────────────────────────

  /**
   * Set the LV95 origin from a point cloud
   */
  setOriginFromPointCloud(pointCloud: { pcoGeometry?: { offset?: THREE.Vector3 } }): void {
    const origin = SWISS_PROJECTION.readOrigin(pointCloud);
    if (origin) {
      this.lv95Origin = origin;
    }
  }

  /**
   * Resolve the LV95 origin, falling back through point cloud offset and the
   * default Swiss projection center. Throws if no usable origin can be built.
   */
  private requireOrigin(): THREE.Vector3 {
    if (this.lv95Origin) {
      return this.lv95Origin;
    }

    const pointClouds = this.viewer?.scene?.pointclouds ?? [];
    this.lv95Origin = SWISS_PROJECTION.resolveOrigin(pointClouds);
    return this.lv95Origin;
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Load terrain tiles
   */
  async loadTerrain(): Promise<void> {
    if (this.loading) {
      return;
    }

    // Increment generation so any previous load pass is ignored.
    this.generation++;

    const origin = this.requireOrigin();

    this.loading = true;

    try {
      this.terrainGroup.position.set(origin.x, origin.y, origin.z);

      this.enabled = true;
      this.loading = false;
      this.overlay.setTerrainEnabled(true);

      this.viewer.dispatchEvent({
        type: 'terrain_loaded',
        manager: this,
      });
    } catch (error) {
      console.error('Error loading quantized mesh terrain:', error);
      this.loading = false;
      this.enabled = false;
      throw error;
    }
  }

  /**
   * Load and verify layer metadata
   */
  async loadLayerMetadata(): Promise<void> {
    try {
      const layerUrl = new URL(
        '../layer.json',
        environment.terrain.quantizedMeshBaseUrl,
      ).toString();
      const response = await fetch(layerUrl);
      if (!response.ok) {
        console.warn('Could not load layer metadata:', response.status);
        return;
      }

      this.metadata = await response.json();
    } catch (error) {
      console.warn('Error loading layer metadata:', error);
    }
  }

  /**
   * Enable or disable terrain
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.terrainGroup.visible = enabled;
    this.overlay.setTerrainEnabled(enabled);
    if (!enabled) {
      this.lastCameraLon = null;
      for (const controller of this.loadingTiles.values()) controller.abort();
      for (const entry of this.vectorTileLayers.values()) entry.provider.abortPending();
    } else {
      for (const [tileKey, mesh] of this.activeTiles) {
        for (const layerId of this.vectorTileLayers.keys()) {
          void this.syncVectorTileLayerTile(
            layerId,
            tileKey,
            this.lod.getTileBounds(mesh.userData.x, mesh.userData.y, mesh.userData.zoom),
            mesh.userData.zoom,
          );
        }
      }
    }
  }

  /**
   * Check if terrain is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Update terrain (called every frame)
   */
  update(): void {
    if (!this.enabled) {
      // Imagery selection awaits installation before it re-enables terrain.
      this.workQueue.update();
      return;
    }

    if (this.viewer.scene?.view?.position) {
      const now = Date.now();
      // Use shorter interval until first tiles are loaded
      const interval = this.activeTiles.size === 0 ? 100 : 500;
      if (!this.lastUpdate || now - this.lastUpdate > interval) {
        void this.updateVisibleTiles();
        this.lastUpdate = now;
      }
    }

    this.overlay.update();

    for (const entry of this.vectorTileLayers.values()) {
      entry.overlay.update();
    }
    this.draper.update();
    const camera = this.viewer.scene.getActiveCamera() ?? undefined;
    this.overlay.flush(camera);
    for (const entry of this.vectorTileLayers.values()) {
      entry.overlay.flush(camera);
    }
    this.workQueue.update();
  }

  /**
   * Dispose of all terrain resources and stop the loading pipeline.
   */
  dispose(): void {
    this.generation++;

    // Abort every in-flight tile load before we tear down the scene graph.
    for (const controller of this.loadingTiles.values()) {
      controller.abort();
    }
    this.loadingTiles.clear();

    // Drain the pending load queue so no new loads start after disposal.
    this.loadQueue.length = 0;
    this.activeLoadCount = 0;

    for (const tileKey of [...this.activeTiles.keys()]) {
      this.removeTile(tileKey);
    }

    this.textures.clear();
    this.overlay.dispose();
    this.loader.clearCache();

    if (this.viewer.scene?.scene) {
      this.viewer.scene.scene.remove(this.terrainGroup);
      this.viewer.scene.scene.remove(this.overlay.group);

      for (const entry of this.vectorTileLayers.values()) {
        this.viewer.scene.scene.remove(entry.overlay.group);
      }
    }

    for (const entry of this.vectorTileLayers.values()) {
      entry.overlay.dispose();
      entry.provider.clear();
    }
    this.vectorTileLayers.clear();
    for (const controller of this.textureReloads.values()) controller.abort();
    this.textureReloads.clear();
    this.preparation.dispose();
    this.workQueue.clear();

    this.enabled = false;
    this.loading = false;
  }

  // ─── Tile scheduling ───────────────────────────────────────────────────────

  /**
   * Update visible tiles using multiple LOD rings.
   */
  async updateVisibleTiles(): Promise<void> {
    if (this.updateInFlight) {
      return;
    }
    this.updateInFlight = true;
    try {
      await this.doUpdateVisibleTiles();
    } catch (err) {
      console.error('Error updating visible tiles:', err);
    } finally {
      this.updateInFlight = false;
    }
  }

  private async doUpdateVisibleTiles(): Promise<void> {
    if (!this.enabled && !this.loading) {
      return;
    }

    const camera = this.viewer.scene.getActiveCamera();
    if (!camera) {
      return;
    }

    const { lon, lat, height } = this.cameraGeoPosition(camera);

    if (!this.cameraMovedSignificantly(lon, lat, height)) {
      // The tile set is unchanged, but tiles left over from the previous
      // camera position still have to expire
      this.evictStaleTiles();
      return;
    }
    this.lastCameraLon = lon;
    this.lastCameraLat = lat;
    this.lastCameraHeight = height;

    const cameraDirection = this.lod.getCameraViewDirection(camera);
    const candidates = this.lod.planVisibleTiles(lon, lat, height, cameraDirection);

    const newVisibleTiles = new Set<string>();
    const tilesToLoad: QueuedTile[] = [];

    for (const tile of candidates) {
      newVisibleTiles.add(tile.tileKey);

      if (this.activeTiles.has(tile.tileKey) || this.loadingTiles.has(tile.tileKey)) {
        continue;
      }

      // Allow retry of failed tiles after a delay
      const failedAt = this.failedTiles.get(tile.tileKey);
      if (failedAt !== undefined) {
        if (Date.now() - failedAt < this.failedTileRetryDelay) {
          continue;
        }
        // Retry: refresh timestamp (overwritten on real failure, removed on success)
        this.failedTiles.set(tile.tileKey, Date.now());
      }

      tilesToLoad.push(tile);
    }

    this.visibleTiles = newVisibleTiles;
    this.enqueueTileLoads(tilesToLoad);

    this.evictStaleTiles();

    // Abort in-flight loads for tiles that are no longer visible. This frees
    // the connection budget for tiles the camera is actually looking at.
    for (const [tileKey, controller] of this.loadingTiles) {
      if (!this.visibleTiles.has(tileKey)) {
        controller.abort();
      }
    }
  }

  /**
   * Camera position expressed as WGS84 lon/lat plus LV95 height.
   */
  private cameraGeoPosition(camera: THREE.Camera): { lon: number; lat: number; height: number } {
    const cameraPos = camera.position;
    const origin = this.requireOrigin();

    // Positions beyond this magnitude are already absolute LV95 metres rather
    // than offsets from the terrain origin.
    const isAbsolute = Math.abs(cameraPos.x) > 1000000 || Math.abs(cameraPos.y) > 1000000;
    const x = isAbsolute ? cameraPos.x : cameraPos.x + origin.x;
    const y = isAbsolute ? cameraPos.y : cameraPos.y + origin.y;
    const height = isAbsolute ? cameraPos.z : cameraPos.z + origin.z;

    const [lon, lat] = SWISS_PROJECTION.lv95ToWgs84([x, y]);
    return { lon, lat, height };
  }

  private cameraMovedSignificantly(lon: number, lat: number, height: number): boolean {
    if (
      this.lastCameraLon === null ||
      this.lastCameraLat === null ||
      this.lastCameraHeight === null
    ) {
      return true;
    }
    return (
      Math.abs(lon - this.lastCameraLon) >= CAMERA_MOVE_EPSILON_DEG ||
      Math.abs(lat - this.lastCameraLat) >= CAMERA_MOVE_EPSILON_DEG ||
      Math.abs(height - this.lastCameraHeight) >= CAMERA_MOVE_EPSILON_M
    );
  }

  /**
   * Enqueue tile loads, merging with existing queue to avoid dropping pending tiles.
   */
  private enqueueTileLoads(tilesToLoad: QueuedTile[]): void {
    // Merge: keep queued tiles that are still visible, add new ones
    const existingKeys = new Set(tilesToLoad.map(t => t.tileKey));
    const retained = this.loadQueue.filter(
      t =>
        !existingKeys.has(t.tileKey) &&
        this.visibleTiles.has(t.tileKey) &&
        !this.activeTiles.has(t.tileKey) &&
        !this.loadingTiles.has(t.tileKey) &&
        !this.failedTiles.has(t.tileKey),
    );
    this.loadQueue = [...tilesToLoad, ...retained];
    this.loadQueue.sort((a, b) => b.priority - a.priority);
    this.processLoadQueue();
  }

  /**
   * Process the load queue with concurrency limiting.
   */
  private processLoadQueue(): void {
    while (this.loadQueue.length > 0 && this.activeLoadCount < this.maxConcurrentLoads) {
      const tile = this.loadQueue.shift();
      if (!tile) {
        break;
      }

      if (
        this.activeTiles.has(tile.tileKey) ||
        this.loadingTiles.has(tile.tileKey) ||
        this.failedTiles.has(tile.tileKey)
      ) {
        continue;
      }

      this.activeLoadCount++;
      const generation = this.generation;
      this.loadTile(tile.z, tile.x, tile.y).finally(() => {
        this.activeLoadCount--;
        if (generation === this.generation && this.enabled) {
          this.processLoadQueue();
        }
      });
    }
  }

  /**
   * Remove tiles that are no longer part of the visible set. Tiles are kept for
   * a short grace period so that newly requested tiles have time to load
   * without leaving holes, then removed so that stale coarse tiles from earlier
   * camera positions cannot overlap and blur the current high detail tiles.
   */
  private evictStaleTiles(): void {
    const nowMs = Date.now();
    const baseZoom = this.lod.calculateZoomLevel(this.lastCameraHeight ?? 0);
    const staleTiles: { tileKey: string; lastSeen: number; zoom: number }[] = [];

    for (const [tileKey, mesh] of this.activeTiles) {
      if (this.visibleTiles.has(tileKey)) {
        mesh.userData.lastSeen = nowMs;
      } else if (mesh.userData.lastSeen === undefined) {
        mesh.userData.lastSeen = nowMs;
      }
      staleTiles.push({ tileKey, lastSeen: mesh.userData.lastSeen, zoom: mesh.userData.zoom });
    }

    staleTiles.sort((a, b) => a.lastSeen - b.lastSeen);

    const remaining: { tileKey: string; lastSeen: number; zoom: number }[] = [];
    for (const stale of staleTiles) {
      if (nowMs - stale.lastSeen > this.staleTileGrace) {
        this.removeTile(stale.tileKey);
      } else {
        remaining.push(stale);
      }
    }

    // Enforce the active tile cap, dropping the least recently seen first.
    // Never evict a tile at the current base zoom to avoid holes under the camera.
    for (const stale of remaining) {
      if (this.activeTiles.size <= this.maxActiveTiles) {
        break;
      }
      if (stale.zoom === baseZoom) {
        continue;
      }
      this.removeTile(stale.tileKey);
    }

    // If we are still over the cap (e.g. because every tile is at base zoom),
    // fall back to evicting the oldest tile regardless of zoom.
    for (const stale of remaining) {
      if (this.activeTiles.size <= this.maxActiveTiles) {
        break;
      }
      this.removeTile(stale.tileKey);
    }
  }

  // ─── Tile loading ──────────────────────────────────────────────────────────

  /**
   * Load a single terrain tile
   */
  async loadTile(z: number, x: number, y: number): Promise<void> {
    const tileKey = `${z}-${x}-${y}`;

    if (this.activeTiles.has(tileKey) || this.loadingTiles.has(tileKey)) {
      return;
    }

    const generation = this.generation;
    const controller = new AbortController();
    const signal = controller.signal;
    this.loadingTiles.set(tileKey, controller);

    const markDone = (): void => {
      if (this.loadingTiles.get(tileKey) === controller) this.loadingTiles.delete(tileKey);
      if (signal.aborted && this.visibleTiles.has(tileKey) && !this.activeTiles.has(tileKey)) {
        this.lastCameraLon = null;
        this.lastUpdate = 0;
      }
    };
    const isStale = (): boolean => generation !== this.generation || signal.aborted;
    let textureResult: WmtsTextureResult | null = null;
    let textureAssigned = false;

    try {
      const url = `${this.baseUrl}/${z}/${x}/${y}.terrain?v=${this.apiVersion}`;
      const buffer = await this.loader.loadBuffer(url, signal);

      if (isStale()) {
        markDone();
        return;
      }

      if (!buffer) {
        markDone();
        this.failedTiles.set(tileKey, Date.now());
        return;
      }

      const tileBounds = this.lod.getTileBounds(x, y, z);
      textureResult = await this.textures.loadTexture(tileBounds, z, MAX_ZOOM, signal);

      if (isStale() || !this.visibleTiles.has(tileKey) || !this.enabled) {
        markDone();
        return;
      }

      const origin = this.requireOrigin();
      const prepared = await this.preparation.terrain(
        {
          kind: 'terrain',
          buffer,
          bounds: tileBounds,
          origin: [origin.x, origin.y, origin.z],
          textureBounds: textureResult?.wmtsBounds,
        },
        signal,
      );
      const install = function* (manager: QuantizedMeshTerrainManager): Generator<void> {
        if (isStale() || !manager.visibleTiles.has(tileKey) || !manager.enabled) return;
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(prepared.positions, 3));
        geometry.setAttribute('uv', new THREE.BufferAttribute(prepared.uvs, 2));
        geometry.setIndex(new THREE.BufferAttribute(prepared.indices, 1));
        geometry.boundingBox = new THREE.Box3(
          new THREE.Vector3(...prepared.bounds.min),
          new THREE.Vector3(...prepared.bounds.max),
        );
        geometry.boundingSphere = geometry.boundingBox.getBoundingSphere(new THREE.Sphere());
        const mesh = Object.assign(
          new THREE.Mesh(
            geometry,
            new THREE.MeshBasicMaterial({
              color: 0xffffff,
              map: textureResult?.texture ?? null,
              side: THREE.DoubleSide,
            }),
          ),
          {
            userData: {
              tileKey,
              zoom: z,
              x,
              y,
              lastSeen: Date.now(),
              textureCacheKey: textureResult?.cacheKey,
              originalPositions: prepared.originalPositions,
            },
          },
        );
        manager.applyLodDepthBias(mesh, z);
        manager.activeTiles.set(tileKey, mesh);
        manager.vectorRequests.set(tileKey, new AbortController());
        manager.terrainGroup.add(mesh);
        manager.draper.addTile(mesh);
        manager.totalVertices += prepared.positions.length / 3;
        textureAssigned = true;
        for (const layerId of manager.vectorTileLayers.keys()) {
          void manager.syncVectorTileLayerTile(layerId, tileKey, tileBounds, z);
        }
        yield;
      };
      await this.workQueue.run(install(this), signal);
      markDone();
    } catch (error) {
      if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
        // Expected during camera movement or disposal; do not poison retry state.
        markDone();
        return;
      }
      console.error(`Error loading tile ${tileKey}:`, error);
      markDone();
      this.failedTiles.set(tileKey, Date.now());
    } finally {
      if (textureResult && !textureAssigned)
        this.textures.release(textureResult.cacheKey, textureResult.texture);
    }
  }

  /**
   * Push coarser tiles away from the camera so that wherever LOD levels still
   * overlap, the finer tile wins the depth test instead of z-fighting.
   */
  private applyLodDepthBias(mesh: TerrainTileMesh, zoom: number): void {
    const bias = Math.max(0, MAX_ZOOM - zoom);
    mesh.material.polygonOffset = true;
    mesh.material.polygonOffsetFactor = bias * 2;
    mesh.material.polygonOffsetUnits = bias * 4;
    mesh.renderOrder = bias;
  }

  /**
   * Remove a tile from the scene
   */
  removeTile(tileKey: string): void {
    const mesh = this.activeTiles.get(tileKey);
    if (!mesh) {
      return;
    }

    this.draper.removeTile(mesh);
    this.vectorRequests.get(tileKey)?.abort();
    this.vectorRequests.delete(tileKey);
    this.textureReloads.get(mesh)?.abort();
    this.textureReloads.delete(mesh);
    this.terrainGroup.remove(mesh);

    this.totalVertices = Math.max(
      0,
      this.totalVertices - (mesh.geometry.attributes.position?.count ?? 0),
    );

    mesh.geometry.dispose();

    if (mesh.userData.textureCacheKey) {
      this.textures.release(mesh.userData.textureCacheKey, mesh.material.map ?? undefined);
    }
    mesh.material.dispose();

    for (const entry of this.vectorTileLayers.values()) {
      entry.overlay.removeTile(tileKey);
    }

    this.activeTiles.delete(tileKey);
  }

  // ─── Texture layer ─────────────────────────────────────────────────────────

  /**
   * Set the active WMTS texture layer and reload textures on existing tiles
   */
  async setTextureLayer(layer: 'map' | 'aerial'): Promise<void> {
    if (!this.textures.setLayerName(layer)) {
      return;
    }

    // Increment generation so in-flight texture loads from the previous layer
    // bail out before assigning stale textures.
    this.generation++;
    for (const controller of this.loadingTiles.values()) controller.abort();
    for (const controller of this.textureReloads.values()) controller.abort();
    this.lastCameraLon = null;

    await Promise.all(
      Array.from(this.activeTiles, ([tileKey, mesh]) =>
        this.reloadTileTexture(mesh).catch((err: Error) => {
          if (err.name === 'AbortError') return;
          console.warn(`Failed to reload texture for tile ${tileKey}:`, err);
        }),
      ),
    );
  }

  /**
   * Get the current texture layer name
   */
  getTextureLayer(): 'map' | 'aerial' {
    return this.textures.getLayerName();
  }

  private async reloadTileTexture(mesh: TerrainTileMesh): Promise<void> {
    const { zoom: z, x, y, tileKey } = mesh.userData;
    const tileBounds = this.lod.getTileBounds(x, y, z);
    this.textureReloads.get(mesh)?.abort();
    const controller = new AbortController();
    this.textureReloads.set(mesh, controller);
    const { signal } = controller;
    const generation = this.generation;
    let result: WmtsTextureResult | null = null;
    let assigned = false;
    try {
      result = await this.textures.loadTexture(tileBounds, z, MAX_ZOOM, signal);
      if (!result) return;
      const original = mesh.userData.originalPositions;
      if (!original) throw new Error(`Missing texture coordinates for terrain tile ${tileKey}`);
      const uvs = await this.preparation.uvs(original, result.wmtsBounds, signal);
      const texture = result;
      const apply = function* (manager: QuantizedMeshTerrainManager): Generator<void> {
        if (generation !== manager.generation || manager.activeTiles.get(tileKey) !== mesh) return;
        const previousTexture = mesh.material.map;
        const previousKey = mesh.userData.textureCacheKey;
        mesh.geometry.attributes.uv.array.set(uvs);
        mesh.geometry.attributes.uv.needsUpdate = true;
        if (previousTexture !== texture.texture) {
          mesh.material.map = texture.texture;
          mesh.material.needsUpdate = true;
        }
        mesh.userData.textureCacheKey = texture.cacheKey;
        assigned = true;
        if (previousKey) manager.textures.release(previousKey, previousTexture ?? undefined);
        yield;
      };
      await this.workQueue.run(apply(this), signal);
    } finally {
      if (result && !assigned) this.textures.release(result.cacheKey, result.texture);
      if (this.textureReloads.get(mesh) === controller) this.textureReloads.delete(mesh);
    }
  }

  // ─── Digital twin feature overlays ─────────────────────────────────────────

  setDigitalTwinFeatures(features: DigitalTwinFeature[]): Promise<void> {
    return this.overlay.setFeatures(features);
  }

  clearDigitalTwinFeatures(): void {
    this.overlay.clear();
  }

  findNearestFeature(
    x: number,
    y: number,
    maxDistance: number,
  ): { guid: string; name: string; distance: number; lon: number; lat: number } | null {
    return this.overlay.findNearest(x, y, maxDistance);
  }

  setFeaturesVisible(visible: boolean): void {
    this.overlay.setVisible(visible);
  }

  setFeatureVisible(guid: string, visible: boolean): void {
    this.overlay.setFeatureVisible(guid, visible);
  }

  setFeatureLineWidth(width: number): void {
    this.overlay.setLineWidth(width);
  }

  // ─── Internal vector tile layers ───────────────────────────────────────────

  /**
   * Enable or disable draping an internal vector tile layer onto the terrain.
   * `config` is only required the first time a layer is enabled; the layer's
   * overlay and feature cache are then kept (just hidden) across further
   * disable/enable calls, so re-enabling does not re-fetch tiles already
   * loaded for the resident terrain tiles.
   */
  setVectorTileLayerEnabled(
    layerId: string,
    enabled: boolean,
    config?: Pick<VectorTileLayerConfig, 'uri' | 'maxZoom' | 'color'> & {
      requestTile: VectorTileRequester;
    },
  ): void {
    let entry = this.vectorTileLayers.get(layerId);
    if (!entry) {
      if (!enabled || !config) return;

      entry = {
        uri: config.uri,
        maxZoom: config.maxZoom,
        requestTile: config.requestTile,
        overlay: new VectorTileOverlay(this.buildOverlayContext(), layerId, config.color),
        provider: new VectorTileFeatureProvider(this.preparation, () => {
          const origin = this.requireOrigin();
          return [origin.x, origin.y, origin.z];
        }),
        enabled: false,
      };
      this.vectorTileLayers.set(layerId, entry);

      if (this.viewer.scene?.scene) {
        this.viewer.scene.scene.add(entry.overlay.group);
      }
    }

    entry.enabled = enabled;
    entry.overlay.setVisible(enabled);
    if (!enabled) entry.provider.abortPending();

    if (enabled) {
      // Load features for every terrain tile already resident; new tiles are
      // picked up as they load via loadTile().
      for (const [tileKey, mesh] of this.activeTiles) {
        const { zoom, x, y } = mesh.userData;
        void this.syncVectorTileLayerTile(
          layerId,
          tileKey,
          this.lod.getTileBounds(x, y, zoom),
          zoom,
        );
      }
    }
  }

  isVectorTileLayerEnabled(layerId: string): boolean {
    return this.vectorTileLayers.get(layerId)?.enabled ?? false;
  }

  /**
   * Load (or reload) the features of a single vector tile layer for a single
   * terrain tile, at the same zoom as the terrain tile capped by the layer's
   * own max zoom.
   */
  private async syncVectorTileLayerTile(
    layerId: string,
    tileKey: string,
    tileBounds: GeoBounds,
    terrainZoom: number,
  ): Promise<void> {
    const entry = this.vectorTileLayers.get(layerId);
    if (!entry?.enabled || entry.overlay.hasTile(tileKey)) return;

    const mesh = this.activeTiles.get(tileKey);
    const vectorZoom = Math.min(terrainZoom, entry.maxZoom);

    try {
      const featuresByVectorTile = await entry.provider.loadFeaturesForBounds(
        entry.uri,
        entry.requestTile,
        tileBounds,
        vectorZoom,
        this.vectorRequests.get(tileKey)?.signal,
      );

      if (!entry.enabled || this.activeTiles.get(tileKey) !== mesh) {
        return;
      }
      entry.overlay.setTileFeatures(tileKey, featuresByVectorTile);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn(`Failed to load vector tile layer "${layerId}" for tile ${tileKey}:`, error);
    }
  }
}
